import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLiveTaskNames } from "@/hooks/usePeople";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useUserRole } from "./useUserRole";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";

export type TaskAlert = Tables<"task_assignments">;

// ── Unified type for merged inbox ───────────────────────────────────────────

export interface UnifiedTaskAlert {
  id: string;
  source: "general" | "npd";
  title: string;
  description: string | null;
  status: string;
  priority: string;
  due_date: string | null;
  created_at: string;
  updated_at: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_by: string | null;
  assigned_by_name: string | null;
  // General-only (null for NPD)
  resolution: string | null;
  escalation_hours: number;
  escalated_at: string | null;
  decline_reason: string | null;
  decline_comment: string | null;
  resolved_at: string | null;
  completion_note: string | null;
  context_type: string | null;
  context_id: string | null;
  context_label: string | null;
  /** What the task is ABOUT, when it points at a record: e.g. 'batch_release_record' + its id. */
  reference_type: string | null;
  reference_id: string | null;
  reassigned_to: string | null;
  reassigned_to_name: string | null;
  /** Login id of who it was escalated to — lets the live-name transform refresh the name. */
  escalated_to: string | null;
  escalated_to_name: string | null;
  // NPD-only (null for general)
  formulation_id: string | null;
  formulation_code: string | null;
  product_name: string | null;
  category: string | null;
  is_gate: boolean;
  sub_phase: string | null;
}

interface NpdTaskWithJoins {
  id: string;
  title: string;
  description: string | null;
  status: string | null;
  priority: string | null;
  due_date: string | null;
  created_at: string;
  updated_at: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  created_by: string | null;
  category: string | null;
  is_gate: boolean | null;
  sub_phase: string | null;
  formulation_id: string | null;
  escalation_hours: number | null;
  escalated_at: string | null;
  formulation: { id: string; formulation_code: string | null; product_name: string | null } | null;
}

export function normalizeGeneralAlert(row: TaskAlert): UnifiedTaskAlert {
  return {
    id: row.id,
    source: "general",
    title: row.title,
    description: row.description,
    status: row.status,
    priority: row.priority,
    due_date: row.due_date,
    created_at: row.created_at,
    updated_at: row.updated_at,
    assigned_to: row.assigned_to,
    assigned_to_name: row.assigned_to_name,
    assigned_by: row.assigned_by,
    assigned_by_name: row.assigned_by_name,
    resolution: row.resolution,
    escalation_hours: row.escalation_hours ?? 48,
    escalated_at: row.escalated_at,
    decline_reason: row.decline_reason,
    decline_comment: row.decline_comment,
    resolved_at: row.resolved_at,
    completion_note: row.completion_note,
    context_type: row.context_type,
    context_id: row.context_id,
    context_label: row.context_label,
    reference_type: row.reference_type,
    reference_id: row.reference_id,
    reassigned_to: row.reassigned_to,
    reassigned_to_name: row.reassigned_to_name,
    escalated_to: row.escalated_to,
    escalated_to_name: row.escalated_to_name,
    formulation_id: null,
    formulation_code: null,
    product_name: null,
    category: null,
    is_gate: false,
    sub_phase: null,
  };
}

function normalizeNpdAlert(row: NpdTaskWithJoins): UnifiedTaskAlert {
  return {
    id: row.id,
    source: "npd",
    title: row.title,
    description: row.description,
    status: row.status ?? "todo",
    priority: row.priority ?? "medium",
    due_date: row.due_date,
    created_at: row.created_at,
    updated_at: row.updated_at,
    assigned_to: row.assigned_to,
    assigned_to_name: row.assigned_to_name,
    assigned_by: row.created_by,
    assigned_by_name: null,
    resolution: row.status === "completed" ? "done" : null,
    escalation_hours: row.escalation_hours ?? 48,
    escalated_at: row.escalated_at,
    decline_reason: null,
    decline_comment: null,
    resolved_at: null,
    completion_note: null,
    context_type: null,
    context_id: null,
    context_label: null,
    reference_type: null,
    reference_id: null,
    reassigned_to: null,
    reassigned_to_name: null,
    escalated_to: null,
    escalated_to_name: null,
    formulation_id: row.formulation?.id ?? row.formulation_id,
    formulation_code: row.formulation?.formulation_code ?? null,
    product_name: row.formulation?.product_name ?? null,
    category: row.category,
    is_gate: row.is_gate ?? false,
    sub_phase: row.sub_phase,
  };
}

// npd_tasks.created_by has no FK to profiles — cannot use PostgREST embedded
// select. Assigner name is resolved client-side from npd_task_assignments or
// left null (UI handles gracefully).
const NPD_SELECT = `*, formulation:formulation_id (id, formulation_code, product_name)`;

// ── Query keys ───────────────────────────────────────────────────────────────

const KEYS = {
  inbox: "task-alerts-inbox",
  sent: "task-alerts-sent",
  completed: "task-alerts-completed",
  stats: "task-alerts-stats",
} as const;

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: [KEYS.inbox] });
  qc.invalidateQueries({ queryKey: [KEYS.sent] });
  qc.invalidateQueries({ queryKey: [KEYS.completed] });
  qc.invalidateQueries({ queryKey: [KEYS.stats] });
  // Also invalidate legacy keys used by Dashboard widget
  qc.invalidateQueries({ queryKey: ["my-task-assignments"] });
  qc.invalidateQueries({ queryKey: ["all-task-assignments"] });
  qc.invalidateQueries({ queryKey: ["my-unified-tasks"] });
  qc.invalidateQueries({ queryKey: ["notifications"] });
}

// ── Queries ──────────────────────────────────────────────────────────────────

/** Pending tasks assigned to the current user (inbox). */
export function useInboxTasks() {
  const { user } = useAuth();

  const liveNames = useLiveTaskNames<UnifiedTaskAlert>();

  return useQuery({
    queryKey: [KEYS.inbox, user?.id],
    queryFn: async (): Promise<UnifiedTaskAlert[]> => {
      if (!user) return [];

      const [generalRes, npdRes] = await Promise.all([
        supabase
          .from("task_assignments")
          .select("*")
          .eq("assigned_to", user.id)
          .is("resolution", null)
          .order("created_at", { ascending: false }),
        supabase
          .from("npd_tasks")
          .select(NPD_SELECT)
          .eq("assigned_to", user.id)
          .neq("status", "completed")
          .neq("status", "cancelled")
          .order("created_at", { ascending: false }),
      ]);

      if (generalRes.error) throw generalRes.error;
      if (npdRes.error) {
        // NPD access can be role-restricted; don't block the entire inbox.
        console.error("useInboxTasks: npd_tasks query failed", npdRes.error);
      }

      const general = (generalRes.data || []).map(normalizeGeneralAlert);
      const npd = npdRes.error ? [] : (npdRes.data || []).map((r: any) => normalizeNpdAlert(r));

      return [...general, ...npd].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    enabled: !!user,
    select: liveNames,
  });
}

/** Tasks the current user created (sent). */
export function useSentTasks() {
  const { user } = useAuth();

  const liveNames = useLiveTaskNames<UnifiedTaskAlert>();

  return useQuery({
    queryKey: [KEYS.sent, user?.id],
    queryFn: async (): Promise<UnifiedTaskAlert[]> => {
      if (!user) return [];

      const [generalRes, npdRes] = await Promise.all([
        supabase
          .from("task_assignments")
          .select("*")
          .eq("assigned_by", user.id)
          .order("created_at", { ascending: false }),
        supabase
          .from("npd_tasks")
          .select(NPD_SELECT)
          .eq("created_by", user.id)
          .order("created_at", { ascending: false }),
      ]);

      if (generalRes.error) throw generalRes.error;
      if (npdRes.error) throw npdRes.error;

      const general = (generalRes.data || []).map(normalizeGeneralAlert);
      const npd = (npdRes.data || []).map((r: any) => normalizeNpdAlert(r));

      return [...general, ...npd].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    enabled: !!user,
    select: liveNames,
  });
}

/** Resolved tasks where the user was involved. */
export function useCompletedTasks() {
  const { user } = useAuth();

  const liveNames = useLiveTaskNames<UnifiedTaskAlert>();

  return useQuery({
    queryKey: [KEYS.completed, user?.id],
    queryFn: async (): Promise<UnifiedTaskAlert[]> => {
      if (!user) return [];

      const [generalRes, npdRes] = await Promise.all([
        supabase
          .from("task_assignments")
          .select("*")
          .not("resolution", "is", null)
          .or(`assigned_to.eq.${user.id},assigned_by.eq.${user.id}`)
          .order("resolved_at", { ascending: false }),
        supabase
          .from("npd_tasks")
          .select(NPD_SELECT)
          .eq("status", "completed")
          .or(`assigned_to.eq.${user.id},created_by.eq.${user.id}`)
          .order("updated_at", { ascending: false }),
      ]);

      if (generalRes.error) throw generalRes.error;
      if (npdRes.error) throw npdRes.error;

      const general = (generalRes.data || []).map(normalizeGeneralAlert);
      const npd = (npdRes.data || []).map((r: any) => normalizeNpdAlert(r));

      return [...general, ...npd].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    enabled: !!user,
    select: liveNames,
  });
}

/** All unresolved tasks across the org (admin oversight). */
export function useAllOrgTasks() {
  const { isAdmin, isLoading: roleLoading } = useUserRole();

  const liveNames = useLiveTaskNames<UnifiedTaskAlert>();

  return useQuery({
    queryKey: [KEYS.inbox, "all-org"],
    queryFn: async (): Promise<UnifiedTaskAlert[]> => {
      const [generalRes, npdRes] = await Promise.all([
        supabase
          .from("task_assignments")
          .select("*")
          .is("resolution", null)
          .order("created_at", { ascending: false })
          .limit(500),
        supabase
          .from("npd_tasks")
          .select(NPD_SELECT)
          .neq("status", "completed")
          .neq("status", "cancelled")
          .order("created_at", { ascending: false })
          .limit(500),
      ]);

      if (generalRes.error) throw generalRes.error;

      const general = (generalRes.data || []).map(normalizeGeneralAlert);
      const npd = npdRes.error ? [] : (npdRes.data || []).map((r: any) => normalizeNpdAlert(r));

      return [...general, ...npd].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    enabled: !roleLoading && isAdmin,
    select: liveNames,
  });
}

/** Stats for the header cards. */
export function useTaskAlertStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: [KEYS.stats, user?.id],
    queryFn: async () => {
      if (!user) return { needsAction: 0, awaitingOthers: 0, completedToday: 0, avgCloseHours: 0 };

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const [inboxRes, sentPendingRes, completedTodayRes, avgRes,
             npdInboxRes, npdSentRes, npdCompletedTodayRes] = await Promise.all([
        supabase
          .from("task_assignments")
          .select("id", { count: "exact", head: true })
          .eq("assigned_to", user.id)
          .is("resolution", null),
        supabase
          .from("task_assignments")
          .select("id", { count: "exact", head: true })
          .eq("assigned_by", user.id)
          .is("resolution", null),
        supabase
          .from("task_assignments")
          .select("id", { count: "exact", head: true })
          .not("resolution", "is", null)
          .gte("resolved_at", todayStart.toISOString())
          .or(`assigned_to.eq.${user.id},assigned_by.eq.${user.id}`),
        supabase
          .from("task_assignments")
          .select("created_at, resolved_at")
          .not("resolution", "is", null)
          .not("resolved_at", "is", null)
          .or(`assigned_to.eq.${user.id},assigned_by.eq.${user.id}`)
          .order("resolved_at", { ascending: false })
          .limit(50),
        supabase
          .from("npd_tasks")
          .select("id", { count: "exact", head: true })
          .eq("assigned_to", user.id)
          .neq("status", "completed")
          .neq("status", "cancelled"),
        supabase
          .from("npd_tasks")
          .select("id", { count: "exact", head: true })
          .eq("created_by", user.id)
          .neq("status", "completed")
          .neq("status", "cancelled"),
        supabase
          .from("npd_tasks")
          .select("id", { count: "exact", head: true })
          .eq("status", "completed")
          .gte("updated_at", todayStart.toISOString())
          .or(`assigned_to.eq.${user.id},created_by.eq.${user.id}`),
      ]);

      let avgCloseHours = 0;
      if (avgRes.data && avgRes.data.length > 0) {
        const totalMs = avgRes.data.reduce((sum, t) => {
          const created = new Date(t.created_at).getTime();
          const resolved = new Date(t.resolved_at!).getTime();
          return sum + (resolved - created);
        }, 0);
        avgCloseHours = Math.round(totalMs / avgRes.data.length / (1000 * 60 * 60) * 10) / 10;
      }

      return {
        needsAction: (inboxRes.count ?? 0) + (npdInboxRes.count ?? 0),
        awaitingOthers: (sentPendingRes.count ?? 0) + (npdSentRes.count ?? 0),
        completedToday: (completedTodayRes.count ?? 0) + (npdCompletedTodayRes.count ?? 0),
        avgCloseHours,
      };
    },
    enabled: !!user,
  });
}

export function useCompleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, completion_note }: { id: string; completion_note?: string }) => {
      const { error } = await supabase
        .from("task_assignments")
        .update({
          resolution: "done",
          completion_note: completion_note || null,
          resolved_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          status: "done",
        })
        .eq("id", id);
      if (error) throw error;

      // The notification is created server-side by the task_assignments triggers (migration 20400000004000).
    },
    onSuccess: () => {
      toast.success("Task completed.");
      invalidateAll(queryClient);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/** Decline a task with reason and optional comment. */
export function useDeclineTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      decline_reason,
      decline_comment,
    }: {
      id: string;
      decline_reason: string;
      decline_comment?: string;
    }) => {
      const { error } = await supabase
        .from("task_assignments")
        .update({
          resolution: "declined",
          decline_reason,
          decline_comment: decline_comment || null,
          resolved_at: new Date().toISOString(),
          status: "done",
        })
        .eq("id", id);
      if (error) throw error;

      // The notification is created server-side by the task_assignments triggers (migration 20400000004000).
    },
    onSuccess: () => {
      toast.success("Task declined.");
      invalidateAll(queryClient);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/**
 * Reassign a task. One RPC, one transaction (reassign_task, 20400000003000): the
 * original is closed as "reassigned" and the new assignee gets a replacement that keeps
 * the task's context, type and reference, assigned BY the caller. Either both happen or
 * neither does.
 *
 * It used to be two client writes — close the original, then insert a clone with
 * assigned_by = the ORIGINAL SENDER. task_alerts_insert only admits
 * auth.uid() = assigned_by, so for anyone who had not sent the task the insert was
 * refused after the original was already closed, and the task vanished.
 * Names are resolved server-side from profiles.user_id, so none are passed in.
 */
export function useReassignTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reassigned_to }: { id: string; reassigned_to: string }) => {
      const { error } = await supabase.rpc("reassign_task", {
        p_task_id: id,
        p_new_assignee: reassigned_to,
      });
      if (error) throw new Error(reassignErrorMessage(error.message));

      // The notification is created server-side by the task_assignments triggers (migration 20400000004000).
    },
    onSuccess: () => {
      toast.success("Task reassigned.");
      invalidateAll(queryClient);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

/** Create a new task alert. */
export function useCreateTaskAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (task: {
      title: string;
      description?: string;
      assigned_to: string;
      assigned_to_name: string;
      assigned_by: string;
      assigned_by_name: string;
      priority: string;
      due_date?: string;
      context_type?: string;
      context_id?: string;
      context_label?: string;
      escalation_hours?: number;
    }) => {
      const { data, error } = await supabase
        .from("task_assignments")
        .insert({
          ...task,
          escalation_hours: task.escalation_hours ?? 48,
        })
        .select()
        .single();
      if (error) throw error;

      // The notification is created server-side by the task_assignments triggers (migration 20400000004000).

      return data;
    },
    onSuccess: () => {
      toast.success("Task created.");
      invalidateAll(queryClient);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

// Export query keys for use in TaskNotificationListener
export const TASK_ALERT_QUERY_KEYS = KEYS;

/**
 * reassign_task raises "CODE: sentence" (e.g. "NOT_ALLOWED: only the assignee, sender or
 * an admin can reassign this task"). Show the sentence; keep anything unrecognised whole.
 */
export function reassignErrorMessage(raw: string): string {
  const match = /^[A-Z_]+: (.+)$/s.exec(raw);
  return match ? match[1].charAt(0).toUpperCase() + match[1].slice(1) : raw;
}
